import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Space,
  Alert,
  Spin,
  Tag,
  Row,
  Col,
  message,
  AutoComplete,
} from 'antd';
import type { InputRef } from 'antd';
import {
  EnvironmentOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useLocation } from '../contexts/LocationContext';
import { config } from '../config/env';

const { Title, Text } = Typography;

declare global {
  interface Window {
    google?: {
      maps: {
        Map: new (element: HTMLElement, options: Record<string, unknown>) => {
          setCenter: (latLng: { lat: number; lng: number }) => void;
          setZoom: (zoom: number) => void;
          addListener: (event: string, callback: (e: { latLng?: { lat: () => number; lng: () => number } }) => void) => void;
        };
        Marker: new (options: {
          position: { lat: number; lng: number };
          map: unknown;
          draggable: boolean;
        }) => {
          setPosition: (position: { lat: number; lng: number }) => void;
          getPosition: () => { lat: () => number; lng: () => number };
          addListener: (event: string, callback: () => void) => void;
        };
        places: {
          Autocomplete: new (
            input: HTMLInputElement,
            options: Record<string, unknown>
          ) => {
            getPlace: () => {
              geometry?: {
                location: { lat: () => number; lng: () => number };
              };
              formatted_address?: string;
            };
            addListener: (event: string, callback: () => void) => void;
          };
        };
      };
    };
    initMap?: () => void;
  }
}

interface LocationSetupProps {
  onComplete?: () => void;
  showSkip?: boolean;
}

export const LocationSetup: React.FC<LocationSetupProps> = ({
  onComplete,
  showSkip = false,
}) => {
  const { lat, lng, city, state, country, utility, address, updateLocation, refreshLocation } = useLocation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addressOptions, setAddressOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [resolvedLocation, setResolvedLocation] = useState<{
    lat: number;
    lng: number;
    city?: string;
    state?: string;
    country?: string;
    utility?: string;
    tariffId?: string;
    address?: string;
    postalCode?: string;
    timezone?: string;
  } | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<{
    setCenter: (latLng: { lat: number; lng: number }) => void;
    setZoom: (zoom: number) => void;
  } | null>(null);
  const markerRef = useRef<{
    setPosition: (position: { lat: number; lng: number }) => void;
    getPosition: () => { lat: () => number; lng: () => number };
  } | null>(null);
  const autocompleteRef = useRef<InputRef>(null);
  const autocompleteInstanceRef = useRef<{
    getPlace: () => {
      geometry?: {
        location: { lat: () => number; lng: () => number };
      };
      formatted_address?: string;
    };
  } | null>(null);

  const reverseGeocode = useCallback(async (lat: number, lng: number) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${config.apiUrl}/api/location/context`,
        { lat, lng },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const context = response.data.context;
      setResolvedLocation({
        lat,
        lng,
        ...context,
      });

      form.setFieldsValue({
        address: context.address || `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
        city: context.city,
        state: context.state,
        country: context.country,
        postalCode: context.postalCode,
        utility: context.utility,
        tariffId: context.tariffId,
      });
    } catch (error: unknown) {
      console.error('Reverse geocoding failed:', error);
      message.error('Failed to resolve location details');
    } finally {
      setLoading(false);
    }
  }, [form]);

  const geocodeAddress = useCallback(async (address: string, providedLat?: number, providedLng?: number) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      if (providedLat && providedLng) {
        // Use provided coordinates
        await reverseGeocode(providedLat, providedLng);
        return;
      }

      // Geocode via backend
      const response = await axios.post(
        `${config.apiUrl}/api/location/from-address`,
        { address },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const location = response.data.location;
      setResolvedLocation(location);

      form.setFieldsValue({
        address: location.address || address,
        city: location.city,
        state: location.state,
        country: location.country,
        postalCode: location.postalCode,
        utility: location.utility,
        tariffId: location.tariffId,
      });

      // Update map marker
      if (mapInstanceRef.current && markerRef.current) {
        mapInstanceRef.current.setCenter({ lat: location.lat, lng: location.lng });
        mapInstanceRef.current.setZoom(15);
        markerRef.current.setPosition({ lat: location.lat, lng: location.lng });
      }

      message.success('Location resolved successfully');
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } } };
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Geocoding failed:', error);
      message.error(`Failed to geocode address: ${axiosError.response?.data?.error || errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, [form, reverseGeocode]);

  const initializeMap = useCallback(() => {
    if (!window.google || !mapRef.current || mapInstanceRef.current) return;

    const initialLat = lat || 40.7128;
    const initialLng = lng || -74.006;

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: initialLat, lng: initialLng },
      zoom: 12,
      mapTypeControl: true,
      streetViewControl: true,
    });

    mapInstanceRef.current = map;

    // Add marker
    const marker = new window.google.maps.Marker({
      position: { lat: initialLat, lng: initialLng },
      map: map,
      draggable: true,
    });

    markerRef.current = marker;

    // Update address when marker is dragged
    marker.addListener('dragend', () => {
      const position = marker.getPosition();
      reverseGeocode(position.lat(), position.lng());
    });

    // Update marker when map is clicked
    map.addListener('click', (e: { latLng?: { lat: () => number; lng: () => number } }) => {
      if (!e.latLng) return;
      const position = e.latLng;
      marker.setPosition({ lat: position.lat(), lng: position.lng() });
      reverseGeocode(position.lat(), position.lng());
    });

    // Initialize autocomplete
    if (autocompleteRef.current?.input && window.google.maps.places) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        autocompleteRef.current.input,
        {
          types: ['address'],
          fields: ['geometry', 'formatted_address', 'address_components'],
        }
      );

      autocompleteInstanceRef.current = autocomplete;

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          const location = place.geometry.location;
          const lat = location.lat();
          const lng = location.lng();

          map.setCenter({ lat, lng });
          map.setZoom(15);
          marker.setPosition({ lat, lng });

          geocodeAddress(place.formatted_address || '', lat, lng);
        }
      });
    }
  }, [lat, lng, reverseGeocode, geocodeAddress]);

  const handleSearch = async (value: string) => {
    if (!value) {
      setAddressOptions([]);
      return;
    }

    // Use backend geocoding service for suggestions
    try {
      // For now, just show the typed value as an option
      // In production, you'd call a geocoding API for suggestions
      setAddressOptions([{ value, label: value }]);
    } catch (error) {
      console.error('Address search failed:', error);
    }
  };

  const handleSave = async () => {
    if (!resolvedLocation) {
      message.warning('Please select or enter an address first');
      return;
    }

    try {
      setSaving(true);
      await updateLocation({
        lat: resolvedLocation.lat,
        lng: resolvedLocation.lng,
        address: form.getFieldValue('address'),
        city: form.getFieldValue('city'),
        state: form.getFieldValue('state'),
        country: form.getFieldValue('country'),
        postalCode: form.getFieldValue('postalCode'),
        utility: form.getFieldValue('utility'),
        tariffId: form.getFieldValue('tariffId'),
        timezone: resolvedLocation.timezone,
      });

      message.success('Location saved successfully!');
      if (onComplete) {
        onComplete();
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      message.error(`Failed to save location: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          reverseGeocode(position.coords.latitude, position.coords.longitude);
        },
        () => {
          setLoading(false);
          message.error('Failed to get current location');
        },
      );
    } else {
      message.warning('Geolocation is not supported by your browser');
    }
  };

  // Load Google Maps script
  useEffect(() => {
    const apiKey = config.googleMapsApiKey;
    if (!apiKey) {
      console.warn('Google Maps API key not configured');
      return;
    }

    // Check if script is already loaded
    if (window.google && window.google.maps) {
      setMapLoaded(true);
      initializeMap();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
    if (existingScript) {
      existingScript.addEventListener('load', () => {
        setMapLoaded(true);
        initializeMap();
      });
      return;
    }

    // Load Google Maps script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setMapLoaded(true);
      initializeMap();
    };
    script.onerror = () => {
      console.error('Failed to load Google Maps script');
      message.error('Failed to load map. Please check your API key configuration.');
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script if component unmounts
      const scriptToRemove = document.querySelector(`script[src*="maps.googleapis.com"]`);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializeMap]);

  useEffect(() => {
    if (mapLoaded && window.google && window.google.maps) {
      initializeMap();
    }
  }, [mapLoaded, initializeMap]);

  useEffect(() => {
    if (city && state && form) {
      const currentAddress = address || `${city}, ${state}`;
      form.setFieldsValue({
        address: currentAddress,
        city,
        state,
        country,
        utility,
      });
    }
  }, [city, state, country, utility, address, form]);

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <Title level={2}>
        <EnvironmentOutlined /> Set Your Location
      </Title>
      <Text type="secondary">
        Set your location to get accurate weather data, utility rates, and energy insights
      </Text>

      <Card style={{ marginTop: '24px' }}>
        <Form form={form} layout="vertical">
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <Form.Item
                label="Address"
                name="address"
                rules={[{ required: true, message: 'Please enter or select an address' }]}
              >
                  <AutoComplete
                    options={addressOptions}
                    onSearch={handleSearch}
                    onSelect={(value) => {
                      geocodeAddress(value);
                    }}
                    placeholder="Enter your address"
                    style={{ width: '100%' }}
                    disabled={loading}
                  >
                    <Input
                      placeholder="Enter your address"
                      suffix={
                        loading ? (
                          <LoadingOutlined />
                        ) : (
                          <EnvironmentOutlined />
                        )
                      }
                      onPressEnter={(e) => {
                        const value = (e.target as HTMLInputElement).value;
                        if (value) {
                          geocodeAddress(value);
                        }
                      }}
                    />
                  </AutoComplete>
              </Form.Item>

              <Space style={{ marginBottom: '16px' }}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleUseCurrentLocation}
                  loading={loading}
                >
                  Use Current Location
                </Button>
                <Button
                  onClick={() => {
                    const address = form.getFieldValue('address');
                    if (address) {
                      geocodeAddress(address);
                    }
                  }}
                  loading={loading}
                >
                  Search
                </Button>
              </Space>

              {resolvedLocation && (
                <Alert
                  message="Location Resolved"
                  description={
                    <div>
                      <Text strong>Address: </Text>
                      <Text>{resolvedLocation.address || `${resolvedLocation.lat.toFixed(4)}, ${resolvedLocation.lng.toFixed(4)}`}</Text>
                      <br />
                      <Text strong>City: </Text>
                      <Text>{resolvedLocation.city || 'N/A'}</Text>
                      <br />
                      <Text strong>State: </Text>
                      <Text>{resolvedLocation.state || 'N/A'}</Text>
                      <br />
                      <Text strong>Country: </Text>
                      <Text>{resolvedLocation.country || 'N/A'}</Text>
                      <br />
                      {resolvedLocation.utility && (
                        <>
                          <Text strong>Utility: </Text>
                          <Text>{resolvedLocation.utility}</Text>
                          <br />
                        </>
                      )}
                      {resolvedLocation.tariffId && (
                        <>
                          <Text strong>Tariff: </Text>
                          <Tag>{resolvedLocation.tariffId}</Tag>
                        </>
                      )}
                    </div>
                  }
                  type="success"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              )}

              <Form.Item label="City" name="city">
                <Input disabled />
              </Form.Item>

              <Form.Item label="State/Province" name="state">
                <Input disabled />
              </Form.Item>

              <Form.Item label="Country" name="country">
                <Input disabled />
              </Form.Item>

              <Form.Item label="Postal Code" name="postalCode">
                <Input disabled />
              </Form.Item>

              <Form.Item label="Utility" name="utility">
                <Input disabled />
              </Form.Item>

              <Form.Item label="Tariff ID" name="tariffId">
                <Input disabled />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    onClick={handleSave}
                    loading={saving}
                    disabled={!resolvedLocation}
                    icon={<CheckCircleOutlined />}
                  >
                    Save Location
                  </Button>
                  {showSkip && (
                    <Button onClick={onComplete}>Skip for Now</Button>
                  )}
                </Space>
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <div style={{ marginBottom: '16px' }}>
                <Text strong>Map Location</Text>
                <Text type="secondary" style={{ display: 'block', marginTop: '4px' }}>
                  Click on the map or drag the marker to set your location
                </Text>
              </div>
              
              {mapLoaded ? (
                <div
                  ref={mapRef}
                  style={{
                    width: '100%',
                    height: '500px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '500px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                  }}
                >
                  <Spin tip="Loading map..." />
                </div>
              )}

              {resolvedLocation && (
                <div style={{ marginTop: '16px' }}>
                  <Text type="secondary">
                    Coordinates: {resolvedLocation.lat.toFixed(6)}, {resolvedLocation.lng.toFixed(6)}
                  </Text>
                </div>
              )}
            </Col>
          </Row>
        </Form>
      </Card>

      {city && state && (
        <Card style={{ marginTop: '24px' }}>
          <Title level={4}>Current Location</Title>
          <Text>
            Your current location is set to: <strong>{city}, {state}</strong>
            {country && `, ${country}`}
            {utility && ` • ${utility}`}
          </Text>
          <br />
          <Button
            type="link"
            onClick={refreshLocation}
            style={{ padding: 0, marginTop: '8px' }}
          >
            Refresh Location
          </Button>
        </Card>
      )}
    </div>
  );
};

export default LocationSetup;
